import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import SecondaryMenu from '../../menu/secondaryMenu.component';

import {
  currentPeriodSelector,
} from '../../shared/store/selectors';
import { fetchAccounts } from '../../shared/store/actions';

import { PeriodPropTypes } from '../../shared/propTypes/period';
import { OptionsPropType } from '../../shared/propTypes/common';
import { FormSchemaPropTypes, FormikPropTypes } from '../../shared/forms/propTypes';

import FormikProvider from '../../shared/forms/formikProvider.component';
import Form from '../../shared/forms/form.component';
import Button from '../../shared/button.component';
import NavigationPrompt, {
  DISCARD_UNSAVED_CHANGES_TITLE,
  DISCARD_UNSAVED_CHANGES_MESSAGE,
} from '../../shared/navigationPrompt.component';

import useTaxEntriesTree from '../shared/useTaxEntriesTree';
import useBsmContext from '../shared/useBsmContext';

import {
  fetchObligationRates,
  fetchProposedJournals,
  fetchProposedJournalsForReclass,
  resetProposedJournals,
  submitTaxEntry,
  submitReclassBalance,
  fetchTbeErrors,
} from '../store/actions';
import {
  accountsSelector,
  proposedJournalsSelector,
  proposedJournalsForReclassSelector,
  isFetchingProposedJournalsSelector,
  isSubmittingTaxEntrySelector,
  isLoadingTypeDescriptionsSelector,
  isLoadingActivityDescriptionsSelector,
  isLoadingTaxEntriesTreeSelector,
  isLoadingCountryTaxTypesSelector,
  apportionmentRateSelector,
  isLoadingObligationRatesSelector,
  deferredTaxRateSelector,
  tbeErrorsSelector,
  isLoadingTbeErrorsSelector,
} from '../store/selectors';

import schema from './addTaxEntry.schema';
import { customSubmitFactory, resetFieldsExcept } from './helpers';
import JournalGrids, { proposedJournalsPropTypes } from './viewJournalGrids.component';
import ReclassJournalGrids, { proposedReclassJournalsPropTypes } from './viewReclassJournalGrids.component';
import { ClientIsLockedOverlay } from '../../shared/clientLocked/ClientLockedOverlay';
import { useHasRequiredCommands } from '../../shared/authorization/requiresPermission.component';

const formatTopsideFlagInValues = values => (
  {
    ...values,
    topsideFlag: values.topsideFlag === '' ? false : values.topsideFlag,
  });

const renderForm = ({
  setCombination,

  formContext,
  fetchJournals,
  fetchObligationRates,
  resetProposedJournals,
  areJournalsLoaded,
  submitEntry,
  currentPeriod,
  tbeErrors,

  isFetchingProposedJournals,
  isSubmittingTaxEntry,
  isReclassBalances,
  canUnlinkNetGrossAmount,
}) => {
  const AddTaxEntryForm = ({ formik, schema }) => {
    const { values: {
      companyCode,
      taxYear,
      countryTaxType,
      entryType,
      entryActivity,
      bookType,
    } } = formik;

    const {
      companyCodes,
      taxYears,
      countryTaxTypes,
      entryTypes,
      entryActivities,
      bookTypes,
      combination,
    } = useTaxEntriesTree(formik, { forReclass: isReclassBalances });

    useBsmContext(formik);

    useEffect(() => {
      if (companyCode && countryTaxType && combination.allowGross) {
        fetchObligationRates({ periodName: currentPeriod.name, companyCode, countryTaxType });
      }
    }, [combination.allowGross, companyCode, countryTaxType]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      setCombination(combination);
    }, [companyCode, taxYear, countryTaxType, entryType, entryActivity, bookType]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (combination.accountOverride) {
        formik.setFieldValue('account', combination.accountOverride);
      }
    }, [combination.accountOverride]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (combination.allowSelectingFbosFlag) {
        formik.setFieldValue('fbosFlag', true);
      }
    }, [combination.allowSelectingFbosFlag]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (combination.forceTopside) {
        formik.setFieldValue('topsideFlag', true);
      }
    }, [combination.forceTopside]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (areJournalsLoaded) {
        resetProposedJournals();
      }
    }, [formik.values]);

    const currencyCode = useMemo(() => {
      const foundCTT = countryTaxType && countryTaxTypes.find(
        ctt => ctt.value === countryTaxType
      );

      return foundCTT ? foundCTT.currencyCode : '';
    }, [countryTaxType, countryTaxTypes]);


    const glAccounts = combination.glAccounts
      ? combination.glAccounts.map(glAccount => {
        const foundAccount = formContext.accounts.find(({ value }) => value === glAccount);

        return {
          label: foundAccount ? foundAccount.label : glAccount,
          value: glAccount,
        };
      })
      : [];

    const payableTags = useMemo(() => combination.payableTags ? combination.payableTags.map(tag => ({
      value: tag.description,
      label: tag.description,
    })) : [], [combination]);

    const submitForProposedJournals = customSubmitFactory(formik, ({
      amount,
      glAccount,
      ...values
    }) => {
      fetchJournals({
        period: currentPeriod,
        currency: currencyCode,
        netAmount: amount.net,
        grossAmount: amount.gross,
        netToAmount: amount.net * -1,
        grossToAmount: amount.gross * -1,
        glAccountOverride: glAccount,
        allowGross: combination.allowGross,
        ...formatTopsideFlagInValues(values),
        toAccount: combination.requireAccount && !combination.allowFromAccount && isReclassBalances
          ? values.account
          : values.toAccount,
      });
    });

    const submit = customSubmitFactory(formik, async ({
      amount,
      glAccount,
      payableTag,
      ...values
    }) => {
      await submitEntry({
        period: currentPeriod,
        currency: currencyCode,
        netAmount: amount.net,
        grossAmount: amount.gross,
        netToAmount: amount.net * -1,
        grossToAmount: amount.gross * -1,
        glAccountOverride: glAccount,
        ...formatTopsideFlagInValues(values),
        toAccount: combination.requireAccount && !combination.allowFromAccount && isReclassBalances
          ? values.account
          : values.toAccount,
        canUnlinkNetGrossAmount,
        payableTag,
      });
      resetFieldsExcept(formik, ['companyCode', 'taxYear', 'countryTaxType']);
    });

    const shouldShowNavigationPrompt = !!formik.values.entryType;

    const isBusy = [
      isFetchingProposedJournals,
      isSubmittingTaxEntry,
      formContext.isLoadingTaxEntriesTree,
      formContext.isLoadingTypeDescriptions,
      formContext.isLoadingActivityDescriptions,
      formContext.isLoadingCountryTaxTypes,
      formContext.isLoadingObligationRates,
      formContext.isLoadingTbeErrors,
    ].some(Boolean);

    return (
      <div>
        <NavigationPrompt
          show={shouldShowNavigationPrompt}
          title={DISCARD_UNSAVED_CHANGES_TITLE}
          message={DISCARD_UNSAVED_CHANGES_MESSAGE}
        />

        <Form
          formik={formik}
          schema={schema}
          formContext={{
            ...formContext,
            companyCodes,
            taxYears,
            countryTaxTypes,
            entryTypes,
            entryActivities,
            bookTypes,
            glAccounts,
            combination,
            currencyCode,
            payableTags,
            tbeErrors,
          }}
        />
        <div className="buttons-container">
          <Button
            type="button"
            kind="secondary"
            onClick={submitForProposedJournals}
            isLoading={isFetchingProposedJournals}
            disabled={isBusy}
          >
            View Proposed Journals
          </Button>
          <Button
            type="button"
            kind="primary"
            onClick={submit}
            isLoading={isSubmittingTaxEntry}
            disabled={isBusy}
          >
            Submit
          </Button>
        </div>
      </div>
    );
  };

  AddTaxEntryForm.propTypes = {
    formik: FormikPropTypes,
    schema: FormSchemaPropTypes,
  };

  return AddTaxEntryForm;
};

const AddTaxEntryPage = ({
  currentPeriod,

  accounts,

  fetchAccounts,
  fetchProposedJournals,
  fetchProposedJournalsForReclass,
  fetchObligationRates,
  fetchTbeErrors,
  resetProposedJournals,
  submitTaxEntry,
  submitReclassBalance,

  proposedJournals,
  proposedJournalsForReclass,
  stateApportionmentRate,
  deferredTaxRate,
  tbeErrors,

  isFetchingProposedJournals,
  isSubmittingTaxEntry,
  isLoadingTaxEntriesTree,
  isLoadingTypeDescriptions,
  isLoadingActivityDescriptions,
  isLoadingCountryTaxTypes,
  isLoadingObligationRates,
  isLoadingTbeErrors,
}) => {
  const [combination, setCombination] = useState({});
  const [isReclassBalances, setIsReclassBalances] = useState(false);
  const canUnlinkNetGrossAmount = useHasRequiredCommands('canUnlinkNetGrossAmount');

  useEffect(() => {
    fetchAccounts();
    resetProposedJournals();
    fetchTbeErrors(currentPeriod.name);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (combination.allowFromAccount && !isReclassBalances) {
      setIsReclassBalances(true);
    }
  }, [combination.allowFromAccount, isReclassBalances]);

  const toggleReclassBalances = useCallback(() => {
    setIsReclassBalances(!isReclassBalances);
  }, [isReclassBalances]);

  const submitEntry = isReclassBalances
    ? submitReclassBalance
    : submitTaxEntry;

  const fetchJournals = isReclassBalances
    ? fetchProposedJournalsForReclass
    : fetchProposedJournals;

  const areJournalsLoaded = !!proposedJournalsForReclass.length
    || !!proposedJournals.slJournal.length
    || !!proposedJournals.slDeferredBalance.length;

  return (
    <div className="add-tax-entry-page">
      <SecondaryMenu>
        <div className="current-period">{currentPeriod.name}</div>
      </SecondaryMenu>

      <ClientIsLockedOverlay
        message="You are not allowed to add tax entry while the client is locked"
      >
        <div className="container-fluid">
          <div className="page">
            <div className={`add-tax-entry-form page-content-container ${isReclassBalances && 'for-reclass'}`}>
              <h5 className="add-tax-entry-title">
                {isReclassBalances ? 'Reclass Balances' : 'Add Tax Entry'}
                <Button
                  kind="secondary"
                  onClick={toggleReclassBalances}
                >
                  {isReclassBalances
                    ? 'Switch to Add Tax Entry'
                    : 'Switch to Reclass Balances'
                  }
                </Button>
              </h5>

              <FormikProvider
                schema={schema}
                render={renderForm({
                  fetchJournals,
                  resetProposedJournals,
                  areJournalsLoaded,
                  isFetchingProposedJournals,

                  fetchObligationRates,
                  submitEntry,

                  currentPeriod,
                  setCombination,

                  isSubmittingTaxEntry,
                  isReclassBalances,
                  tbeErrors,

                  canUnlinkNetGrossAmount,
                  formContext: {
                    accounts,
                    stateApportionmentRate,
                    deferredTaxRate,

                    isLoadingTaxEntriesTree,
                    isLoadingTypeDescriptions,
                    isLoadingActivityDescriptions,
                    isLoadingCountryTaxTypes,
                    isLoadingObligationRates,
                    isLoadingTbeErrors,

                    isReclassBalances,
                  },
                })}
                validationContext={{ ...combination, isReclassBalances, tbeErrors }}
              />


              {
                isReclassBalances
                  ? (!!proposedJournalsForReclass.length
                  && <ReclassJournalGrids
                    proposedJournals={proposedJournalsForReclass}
                    allowGross={combination.allowGross}
                  />)
                  : (!!proposedJournals.slJournal.length && !!proposedJournals.slDeferredBalance.length
                  && <JournalGrids
                    proposedJournals={proposedJournals}
                    allowGross={combination.allowGross}
                  />)
              }
            </div>
          </div>
        </div>
      </ClientIsLockedOverlay>
    </div>
  );
};

AddTaxEntryPage.propTypes = {
  currentPeriod: PeriodPropTypes,

  accounts: OptionsPropType,

  fetchAccounts: PropTypes.func.isRequired,
  fetchProposedJournals: PropTypes.func.isRequired,
  fetchProposedJournalsForReclass: PropTypes.func.isRequired,
  resetProposedJournals: PropTypes.func.isRequired,
  fetchObligationRates: PropTypes.func.isRequired,
  submitTaxEntry: PropTypes.func.isRequired,
  submitReclassBalance: PropTypes.func.isRequired,
  fetchTbeErrors: PropTypes.func.isRequired,

  proposedJournals: proposedJournalsPropTypes,
  proposedJournalsForReclass: proposedReclassJournalsPropTypes,
  tbeErrors: PropTypes.arrayOf(PropTypes.shape({
    companyCode: PropTypes.string,
    tbeError: PropTypes.string,
  })).isRequired,
  isFetchingProposedJournals: PropTypes.bool.isRequired,
  isSubmittingTaxEntry: PropTypes.bool.isRequired,
  isLoadingTaxEntriesTree: PropTypes.bool.isRequired,
  isLoadingTypeDescriptions: PropTypes.bool.isRequired,
  isLoadingActivityDescriptions: PropTypes.bool.isRequired,
  isLoadingCountryTaxTypes: PropTypes.bool.isRequired,
  isLoadingObligationRates: PropTypes.bool.isRequired,
  isLoadingTbeErrors: PropTypes.bool.isRequired,

  deferredTaxRate: PropTypes.number,
  stateApportionmentRate: PropTypes.number,
};

const mapStateToProps = state => ({
  currentPeriod: currentPeriodSelector(state),
  stateApportionmentRate: apportionmentRateSelector(state),
  deferredTaxRate: deferredTaxRateSelector(state),
  accounts: accountsSelector(state),
  proposedJournals: proposedJournalsSelector(state),
  proposedJournalsForReclass: proposedJournalsForReclassSelector(state),
  tbeErrors: tbeErrorsSelector(state),

  isFetchingProposedJournals: isFetchingProposedJournalsSelector(state),
  isSubmittingTaxEntry: isSubmittingTaxEntrySelector(state),
  isLoadingTypeDescriptions: isLoadingTypeDescriptionsSelector(state),
  isLoadingActivityDescriptions: isLoadingActivityDescriptionsSelector(state),
  isLoadingTaxEntriesTree: isLoadingTaxEntriesTreeSelector(state),
  isLoadingCountryTaxTypes: isLoadingCountryTaxTypesSelector(state),
  isLoadingObligationRates: isLoadingObligationRatesSelector(state),
  isLoadingTbeErrors: isLoadingTbeErrorsSelector(state),
});

export default connect(mapStateToProps, {
  fetchAccounts,
  fetchProposedJournals,
  fetchProposedJournalsForReclass,
  fetchTbeErrors,
  resetProposedJournals,
  fetchObligationRates,
  submitTaxEntry,
  submitReclassBalance,
})(AddTaxEntryPage);
